import { useEffect, useState, useCallback } from 'react';

import {
  TUseLinkedTagsForAssetsProps,
  TAssetTagsResult,
} from '@/shared/features/tags/hooks/use-linked-tags-for-assets/use-linked-tags-for-assets.types';
import { useLazyReadLinkedTagsForAssetQuery } from '@/shared/redux/api/assets/tags/tags-api';

export const useLinkedTagsForAssets = ({
  assetType,
  assetIds,
}: TUseLinkedTagsForAssetsProps) => {
  const [trigger] = useLazyReadLinkedTagsForAssetQuery();
  const [assetTags, setAssetTags] = useState<TAssetTagsResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTags = useCallback(async () => {
    if (!assetType || !assetIds) {
      setAssetTags([]);
      return;
    }

    setIsLoading(true);

    try {
      // preferCacheValue: reuse cached tags so permission checks don't refetch
      const promises = assetIds.map((assetId) =>
        trigger({ assetId, assetType }, true).unwrap(),
      );

      const results = await Promise.all(promises);

      const mappedResults: TAssetTagsResult[] = assetIds.map(
        (assetId, index) => {
          const tags = results[index]?.readLinkedTagsForAsset?.items ?? [];
          return {
            assetId,
            tags: tags.map(({ name }) => name),
          };
        },
      );

      setAssetTags(mappedResults);
    } catch (error) {
      console.error('Failed to fetch tags for assets:', error);
      setAssetTags([]);
    } finally {
      setIsLoading(false);
    }
  }, [assetIds, assetType, trigger]);

  useEffect(() => {
    void fetchTags();
  }, [fetchTags]);

  return {
    assetTags,
    isLoading,
    refetch: fetchTags,
  };
};
